import {
  Box,
  Button,
  ButtonVariants,
  Divider,
  EllipsisText,
  FormControlSizes,
  getIncrementFromScale,
  HStack,
  Icon,
  IconButton,
  IconName,
  InlineFormattedNumber,
  Meter,
  NumberInput,
  Popover,
  resolvePricingModeDisplaySpec,
  SideEnum,
  Stack,
  Text,
  toBigWithDefault,
  ToggleButton,
  Tooltip,
  useCallbackRef,
  useCurrenciesContext,
  useMarketsContext,
  usePopoverState,
  useSecurity,
  type Quote,
  type QuoteMarket,
} from '@talos/kyoko';
import { useEffect, useState } from 'react';
import { RFQSweeper } from '../../../RFQSweeper';

interface PartialFillConfirmTradeParams {
  side: SideEnum;
  price: string;
  orderQty: string;
}

interface PartialFillButtonProps {
  /** The entire quote this button is for */
  quote: Quote;
  /** The specific quote market the button is for */
  quoteMarket: QuoteMarket;
  size?: FormControlSizes;
  buyDisabled?: boolean;
  sellDisabled?: boolean;
  /** Callback called when the user confirms their execution intention */
  onConfirmTrade: (params: PartialFillConfirmTradeParams) => void;
}

/** A button with a popover which lets the user execute a partial fill on a quote's market */
export const PartialFillButton = ({
  quote,
  quoteMarket,
  size = FormControlSizes.Xxs,
  buyDisabled = false,
  sellDisabled = false,
  onConfirmTrade,
}: PartialFillButtonProps) => {
  const { marketsByName } = useMarketsContext();
  const { currenciesBySymbol } = useCurrenciesContext();
  const security = useSecurity(quote.Symbol);
  const currency = quote.Currency ? currenciesBySymbol.get(quote.Currency) : undefined;
  const { priceCurrency, priceDisplaySpec, priceIncrement, toDisplayPrice } = resolvePricingModeDisplaySpec({
    pricingMode: quote.PricingMode,
    security,
  });

  const disabled = buyDisabled && sellDisabled;

  const [sweeper, setSweeper] = useState<RFQSweeper>();
  const popover = usePopoverState({
    trigger: 'click',
    closeOnClickOutside: true,
    placement: 'top-end',
    usePortal: true,
  });
  const togglesDisabled = buyDisabled !== sellDisabled;
  const initialSide = buyDisabled && !sellDisabled ? SideEnum.Sell : SideEnum.Buy;

  const createSweeper = useCallbackRef((side: SideEnum) => {
    return RFQSweeper.create({
      quotes: [{ quote, quoteMarket }],
      side,
      currency,
    }).addMarket(quoteMarket.Market);
  });

  const handleClick = useCallbackRef(() => {
    setSweeper(createSweeper(initialSide));
  });

  const handleQtyChange = useCallbackRef((value: string) => {
    setSweeper(currentSweeper => currentSweeper?.setDesiredQty(quoteMarket.Market, value));
  });

  // When the quote market updates, we need to push that change into the sweeper.
  // The sweeper will then re-validate the user's entire desired qty on the fill, and expose any errors.
  useEffect(() => {
    setSweeper(currentSweeper => currentSweeper?.updateQuotes([{ quote, quoteMarket }]));
  }, [quote, quoteMarket]);

  // In this component, we assume that there will always be just one fill entry
  const fill = sweeper?.fills.at(0);

  const handleConfirm = useCallbackRef(() => {
    if (!sweeper || sweeper.error || !fill?.desiredQty.value) {
      return;
    }

    onConfirmTrade({
      side: sweeper.side,
      price: fill.px,
      orderQty: fill.desiredQty.value,
    });
    popover.close();
  });

  const handleSideChange = useCallbackRef((side: SideEnum) => {
    if (togglesDisabled || side === sweeper?.side) {
      return;
    }

    setSweeper(createSweeper(side));
  });

  return (
    <Popover {...popover} contentStyle={{ padding: 0, width: '380px' }}>
      {/* Popover "opener" / init */}
      <IconButton
        icon={IconName.Scale}
        size={size}
        onClick={handleClick}
        disabled={disabled}
        data-testid={`quote-market-${quoteMarket.MarketAccount}-partial-fill-button`}
      />

      {/* Popover content */}
      <Stack py="spacing8" gap="spacing8">
        {sweeper && fill && (
          <>
            {/* Header */}
            <HStack justifyContent="space-between" gap="spacing4" px="spacing8">
              <HStack gap="spacing4">
                <Text fontWeight={500}>Partial Fill</Text>
                <SideToggle side={sweeper.side} disabled={togglesDisabled} onChange={handleSideChange} />
              </HStack>

              <IconButton icon={IconName.Close} size={FormControlSizes.Xxs} onClick={() => popover.close()} ghost />
            </HStack>

            {/* (Single) Fill details */}
            <HStack gap="spacing8" px="spacing8" justifyContent="space-between">
              <EllipsisText color="colorTextImportant" minWidth="100px" data-testid="partial-fill-market-name">
                {marketsByName.get(fill.quote.quoteMarket.Market)?.DisplayName ?? fill.quote.quoteMarket.Market}
              </EllipsisText>
              <Box data-testid="partial-fill-available-qty">
                <InlineFormattedNumber
                  number={fill.availableQty}
                  currency={quote.Currency ?? 'Units'}
                  increment={currency?.DefaultIncrement ?? null}
                  align="right"
                />
              </Box>
              <NumberInput
                width="120px"
                value={fill.desiredQty.displayValue}
                onChange={handleQtyChange}
                minIncrement={getIncrementFromScale(fill.desiredQty.scale)}
                defaultIncrement={currency?.DefaultIncrement ?? '1'}
                suffix={currency?.Symbol ?? 'UNITS'}
                invalid={fill.desiredQty.hasError}
                disabled={fill.desiredQty.isDisabled}
                size={FormControlSizes.Small}
                data-testid="partial-fill-qty-input"
                autoFocus
              />
              <Box data-testid="partial-fill-price">
                <InlineFormattedNumber
                  number={toDisplayPrice(fill.px)}
                  specification={priceDisplaySpec}
                  increment={priceIncrement}
                  currency={priceCurrency}
                  align="right"
                />
              </Box>
            </HStack>

            <Divider orientation="horizontal" />

            {/* Fill summary */}
            <Box w="100%" px="spacing8">
              <FillSummary desiredQty={fill.desiredQty.value} quote={quote} />
            </Box>

            <HStack w="100%" gap="spacing8" px="spacing8">
              <Button
                onClick={() => {
                  popover.close();
                  setSweeper(undefined);
                }}
                size={FormControlSizes.Small}
              >
                Cancel
              </Button>
              <Divider orientation="vertical" />
              <Tooltip tooltip={sweeper.error} disabled={!sweeper.error} targetStyle={{ width: '100%' }}>
                <Button
                  variant={sweeper.side === SideEnum.Buy ? ButtonVariants.Positive : ButtonVariants.Negative}
                  onClick={handleConfirm}
                  size={FormControlSizes.Small}
                  flex="1"
                  disabled={!!sweeper.error}
                  data-testid="partial-fill-confirm-button"
                >
                  Confirm {sweeper.side}
                </Button>
              </Tooltip>
            </HStack>
          </>
        )}
      </Stack>
    </Popover>
  );
};

function SideToggle({
  side,
  disabled,
  onChange,
}: {
  side: SideEnum;
  disabled: boolean;
  onChange: (side: SideEnum) => void;
}) {
  return (
    <HStack gap="spacing2">
      <ToggleButton
        selected={side === SideEnum.Sell}
        selectedVariant={ButtonVariants.Negative}
        disabled={disabled}
        size={FormControlSizes.Xxs}
        onClick={() => onChange(SideEnum.Sell)}
        data-testid="partial-fill-sell-toggle"
      >
        Sell
      </ToggleButton>
      <ToggleButton
        selected={side === SideEnum.Buy}
        selectedVariant={ButtonVariants.Positive}
        disabled={disabled}
        size={FormControlSizes.Xxs}
        onClick={() => onChange(SideEnum.Buy)}
        data-testid="partial-fill-buy-toggle"
      >
        Buy
      </ToggleButton>
    </HStack>
  );
}

export function FillSummary({
  desiredQty,
  quote,
  layout = 'vertical',
}: {
  desiredQty: string | undefined;
  quote: Quote;
  layout?: 'vertical' | 'horizontal';
}) {
  const desiredQtyBig = desiredQty ? toBigWithDefault(desiredQty, 0) : undefined;
  const orderQty = toBigWithDefault(quote.OrderQty, 0);
  const fillProgress = orderQty.gt(0) ? toBigWithDefault(desiredQtyBig, 0).div(orderQty).toNumber() : 0;
  const isOverfilled = desiredQtyBig != null && orderQty.gt(0) && desiredQtyBig.gt(orderQty);
  const isFullyFilled = desiredQtyBig != null && orderQty.gt(0) && desiredQtyBig.eq(orderQty);
  const summaryColor = isOverfilled
    ? 'colors.red.lighten'
    : isFullyFilled
      ? 'colors.green.lighten'
      : 'colors.yellow.lighten';
  const qtyCurrency = quote.Currency ?? 'Units';
  const summaryText = (
    <HStack gap="spacing4" justifyContent="flex-start">
      <Icon
        icon={isFullyFilled && !isOverfilled ? IconName.CheckCircleSolid : IconName.ExclamationCircleSolid}
        color={summaryColor}
      />
      {isFullyFilled ? (
        <Text>Order fully filled</Text>
      ) : (
        <>
          <Text>{isOverfilled ? 'Overfilled' : 'Partially filled'} -</Text>
          <InlineFormattedNumber number={desiredQty} currency={qtyCurrency} />
          <Text>/</Text>
          <InlineFormattedNumber number={quote.OrderQty} currency={qtyCurrency} />
        </>
      )}
    </HStack>
  );
  const meter = <Meter value={fillProgress} color={summaryColor} showInitialAnimation={false} animate={false} />;

  if (layout === 'horizontal') {
    return (
      <HStack gap="spacing8" w="100%" justifyContent="space-between">
        {summaryText}
        <Box flex="1" maxWidth="250px">
          {meter}
        </Box>
      </HStack>
    );
  }

  return (
    <Stack gap="spacing8">
      {summaryText}
      {meter}
    </Stack>
  );
}
